
import throttle from 'lodash.throttle';
import moment from 'moment';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Input, Popup, Button, Image, Label, Icon } from 'semantic-ui-react';
import { useQuery, useMutation } from '@apollo/client';
import { useCallback, useState, useEffect } from 'react';

import { PATHS } from '../../../../paths';
import { useIsAdmin } from '../../../../hooks/isAdmin';
import { useIsCenterManager } from '../../../../hooks/isCenterManager';
import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { InterimStates } from '../../../../components/InterimStates';
import { PersonTags } from '../../../../components/Protected/PersonTags';
import { UPDATE_USER_FIELD } from '../../../../mutations/UPDATE_USER_FIELDS';
import { DELETE_USER_RELATIONSHIP } from '../../../../mutations/DELETE_USER_RELATIONSHIP';
import { CREATE_SIBLING_RELATIONSHIP } from '../../../../mutations/CREATE_SIBLING_RELATIONSHIP';
import { CREATE_PARENT_RELATIONSHIP } from '../../../../mutations/CREATE_PARENT_RELATIONSHIP';
import { ADMIN_GET_PERSON } from '../data/ADMIN_GET_PERSON';
import { GENDER_OPTIONS } from '../../../../utils/registration';
import { UserPhoneNumber } from '../../../../components/UserPhoneNumber';
import { PersonEmail } from '../../../../components/PersonEmail';
import { GiveLogin, GiveLoginOutcome, type GiveLoginResult } from '../../../../components/GiveLogin';
import { CurrentBlockSection, CoursesSection, TeachingSection } from '../shared/ProfileSections';
import { ConsentSectionCard, SupportingDocumentsCard } from '../shared/RegistrationSections';

import styles from './AdminProfileView.module.css';

// --- Dates of birth ---
//
// A birthday is a bare calendar date, never an instant. The whole of this section
// keeps it as a plain `yyyy-MM-dd` string and never routes it through a Date/moment
// *instant*, because doing so re-interprets it in the browser's timezone and shifts
// the day. That's the same discipline the iOS and Android apps already follow.

// Earliest birthday the picker accepts — older than this is a typo, not a member.
const DOB_MIN = '1900-01-01';

// Today in the browser's *local* timezone: getFullYear/getMonth/getDate are all
// local-time accessors, so an admin never gets handed UTC's yesterday or tomorrow.
const localToday = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// The stored dob arrives as a full ISO string; keep only the date portion so it
// round-trips through <input type="date"> untouched.
const toDateInput = (dob: string | null | undefined): string => (dob ? dob.slice(0, 10) : '');

// Format for display from that same bare date. Handing moment the full ISO string
// would parse it as an instant and render it in local time, showing the day before
// for anyone west of UTC — i.e. disagreeing with the editor directly above it.
const formatDob = (dob: string | null | undefined): string =>
    dob ? moment(toDateInput(dob), 'YYYY-MM-DD').format('MMMM Do Y') : 'unknown';

// --- Sub-components ---

type PersonRef = { id: number; name: string; photoUrl: string };

const RelatedPerson = ({
    person,
    editMode,
    onRemove,
}: {
    person: PersonRef;
    editMode?: boolean;
    onRemove?: () => void;
}) => (
    <div className={styles.relatedPerson}>
        {editMode && onRemove && (
            <Icon name="times circle" color="red" link onClick={onRemove} className={styles.removeIcon} />
        )}
        <Image src={person.photoUrl} circular className={styles.avatarSmall} />
        <Link to={`/people/${person.id}`}>{person.name}</Link>
    </div>
);

const ProfilePhoto = ({
    photoUrl,
    uploadURLSmall,
    canEdit,
    onRefetch,
}: {
    photoUrl: string;
    uploadURLSmall?: string;
    canEdit: boolean;
    onRefetch: () => void;
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [enlarged, setEnlarged] = useState(false);

    const upload = async () => {
        if (!file || !uploadURLSmall) return;
        setUploading(true);
        try {
            await fetch(uploadURLSmall, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': file.size.toString(),
                },
                referrer: 'no-referrer',
            });
            setFile(null);
            onRefetch();
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={styles.photoArea}>
            <Image
                src={photoUrl}
                circular
                className={enlarged ? styles.photoLarge : styles.photoSmall}
                onClick={() => setEnlarged(!enlarged)}
            />
            {canEdit && (
                <div className={styles.photoControls}>
                    {uploading ? (
                        <Label basic loading>Uploading...</Label>
                    ) : (
                        <Label basic>{enlarged ? 'Click to minimize' : 'Click to enlarge'}</Label>
                    )}
                    <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} />
                    <Button compact primary size="tiny" onClick={upload} disabled={!file}>
                        Upload Photo
                    </Button>
                </div>
            )}
        </div>
    );
};

// --- Hooks ---

// Every editable field commits when it is left, never mid-keystroke. These inputs are
// seeded from `data` by the effect below, so a save that lands while someone is still
// typing re-seeds the field from the server and yanks the value out from under them —
// the more so on a slow connection, where the response arrives several words later.
// Saving on blur also keeps intermediate states off the server entirely, which a date
// needs outright: its year passes through 0001 as you type it.
function useFieldOnBlur(
    field: string,
    updateUser: (opts: any) => Promise<any>,
    id: string | undefined,
    refetch: () => void,
) {
    return useCallback(
        async (value: string, original: string) => {
            if (value === original) return;
            await updateUser({ variables: { userId: Number(id), value, field } });
            refetch();
        },
        [field, updateUser, id, refetch]
    );
}

// --- Main component ---

const AdminProfileView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isAdmin = useIsAdmin();
    const isCenterManager = useIsCenterManager();

    const [editMode, setEditMode] = useState(false);
    const [name, setName] = useState('');
    const [scriptName, setScriptName] = useState('');
    const [address, setAddress] = useState('');
    const [dob, setDob] = useState('');
    const [siblingIdentifier, setSiblingIdentifier] = useState('');
    const [parentIdentifier, setParentIdentifier] = useState('');
    const [copied, setCopied] = useState(false);
    // Three states, not two: closed, the form open, and the result. The result must survive
    // until it is dismissed — for a teacher with no email the password is in it and is not
    // recoverable once this unmounts.
    const [givingLogin, setGivingLogin] = useState(false);
    const [loginResult, setLoginResult] = useState<GiveLoginResult | null>(null);

    const { loading, error, data, refetch } = useQuery(ADMIN_GET_PERSON, {
        variables: { personId: Number(id) },
        skip: !id || isNaN(Number(id)),
    });

    const [updateUser] = useMutation(UPDATE_USER_FIELD);
    const [deleteUserRelationship] = useMutation(DELETE_USER_RELATIONSHIP);
    const [createSiblingRelationship] = useMutation(CREATE_SIBLING_RELATIONSHIP);
    const [createParentRelationship] = useMutation(CREATE_PARENT_RELATIONSHIP);

    // Seed the inputs from the server, but never while editing: committing one field
    // refetches, and re-seeding here would overwrite the *other* fields — tab from Name
    // into Script Name and Name's refetch lands on top of what you've started typing.
    // Entering edit mode seeds all four from `user`, so nothing is missed by skipping.
    useEffect(() => {
        if (data?.user && !editMode) {
            setName(data.user.name || '');
            setScriptName(data.user.scriptName || '');
            setAddress(data.user.address || '');
            setDob(toDateInput(data.user.dob));
        }
    }, [data, editMode]);

    const commitName = useFieldOnBlur('name', updateUser, id, refetch);
    const commitScriptName = useFieldOnBlur('scriptName', updateUser, id, refetch);
    const commitAddress = useFieldOnBlur('address', updateUser, id, refetch);
    const commitDob = useFieldOnBlur('dob', updateUser, id, refetch);
    const commitGender = useFieldOnBlur('gender', updateUser, id, refetch);

    const archiveUser = useCallback(
        throttle(
            async () => {
                if (!window.confirm('Are you sure you want to archive this person?')) return;
                await updateUser({ variables: { userId: Number(id), value: 'now', field: 'archivedAt' } });
                navigate('/people');
            },
            500,
            { leading: false, trailing: true }
        ),
        [updateUser, id, navigate]
    );

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy!', err);
        }
    };

    const removeRelationship = (userId2: number, type: 'PARENT' | 'SIBLING') => () =>
        deleteUserRelationship({
            variables: { userId1: Number(id), userId2, relationshipType: type },
        }).then(() => refetch());

    if (loading || error || !data?.user) {
        return <InterimStates loading={loading} error={error} label="person's details" />;
    }

    const { user } = data;
    if (user.id == null) return <div>You don't have permission to view this</div>;

    const canEdit = editMode && (isAdmin || isCenterManager);
    const personId = Number(id);

    const handleDobBlur = () => {
        const original = toDateInput(user.dob);
        // Clearing the field is allowed; anything else has to be a date that could
        // plausibly be a birthday. Comparing the yyyy-MM-dd strings directly is both
        // chronological and timezone-free — parsing them into instants is exactly what
        // this whole path avoids.
        if (dob !== '' && (dob < DOB_MIN || dob > localToday())) {
            setDob(original);
            return;
        }
        commitDob(dob, original);
    };

    const hasRelationships =
        user.buddies.length > 0 || user.parents.length > 0 ||
        user.siblings.length > 0 || user.children.length > 0;

    return (
        <ResponsiveContainer>
            <div className={styles.page}>

                {/* Toolbar */}
                <div className={styles.toolbar}>
                    <Icon name="arrow left" size="large" style={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
                    <div>
                        {user.records?.length > 0 && (
                            <Button basic color="green" as={Link} to={`${PATHS.PEOPLE}/${user.id}/records`}>
                                <Icon name="file alternate" /> View records
                            </Button>
                        )}
                        {(isAdmin || isCenterManager) && !user.isArchived && (
                            <Button basic color="blue" onClick={() => {
                                if (!editMode) {
                                    setName(user.name || '');
                                    setAddress(user.address || '');
                                    setScriptName(user.scriptName || '');
                                    setDob(toDateInput(user.dob));
                                }
                                setEditMode(!editMode);
                            }}>
                                <Icon name="edit" />
                                {editMode ? 'Exit Edit Mode' : 'Edit Profile'}
                            </Button>
                        )}
                    </div>
                </div>

                {user.isArchived && (
                    <Label color="red" size="large" style={{ marginBottom: '1em', display: 'block' }}>
                        Archived Profile
                    </Label>
                )}

                {/* Profile Header */}
                <div className={styles.card}>
                    <div className={styles.profileHeader}>
                        <ProfilePhoto
                            photoUrl={user.photoUrl}
                            uploadURLSmall={user.uploadURLSmall}
                            canEdit={canEdit}
                            onRefetch={refetch}
                        />
                        <div className={styles.profileInfo}>
                            {canEdit ? (
                                <Input
                                    fluid
                                    placeholder="Full Name"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    onBlur={() => commitName(name, user.name || '')}
                                />
                            ) : (
                                <p className={styles.profileName}>{user.name}</p>
                            )}
                            {/* The input always renders in edit mode: gating it on a script
                                name already existing made the field unreachable for exactly
                                the people who need one set. Only the read-only line is
                                conditional, since there is nothing to show without it. */}
                            {canEdit ? (
                                <Input
                                    fluid
                                    placeholder="Script Name"
                                    value={scriptName}
                                    onChange={e => setScriptName(e.target.value)}
                                    onBlur={() => commitScriptName(scriptName, user.scriptName || '')}
                                />
                            ) : (user.scriptName || user.preferredName) && (
                                <p className={styles.profileSubName}>
                                    {user.scriptName} {user.preferredName && `(${user.preferredName})`}
                                </p>
                            )}
                            <p className={styles.profileIdentifier}>ID: {user.identifier}</p>
                        </div>
                        <Popup
                            content="Copied profile URL to clipboard!"
                            open={copied}
                            on="click"
                            trigger={
                                <Button
                                    color={copied ? 'green' : 'teal'}
                                    icon={copied ? 'check' : 'copy'}
                                    content={copied ? 'Copied' : 'Share'}
                                    size="small"
                                    onClick={handleCopy}
                                />
                            }
                        />
                    </div>
                </div>

                {/* Relationships editor (edit mode only) */}
                {canEdit && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Relationships</div>
                        <div className={styles.addRelationship}>
                            <span>Add sibling</span>
                            <Input
                                placeholder="Sibling Identifier"
                                value={siblingIdentifier}
                                onChange={e => setSiblingIdentifier(e.target.value)}
                            />
                            <Button onClick={async () => {
                                if (!siblingIdentifier) return;
                                await createSiblingRelationship({
                                    variables: { newSiblingIdentifier: siblingIdentifier, siblingUserId: personId },
                                });
                                setSiblingIdentifier('');
                                refetch();
                            }}>Add</Button>
                        </div>
                        <div className={styles.addRelationship}>
                            <span>Add parent</span>
                            <Input
                                placeholder="Parent Identifier"
                                value={parentIdentifier}
                                onChange={e => setParentIdentifier(e.target.value)}
                            />
                            <Button onClick={async () => {
                                if (!parentIdentifier) return;
                                await createParentRelationship({
                                    variables: { newParentIdentifier: parentIdentifier, childUserId: personId },
                                });
                                setParentIdentifier('');
                                refetch();
                            }}>Add</Button>
                        </div>
                    </div>
                )}

                {/* About */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>About</div>

                    <div className={styles.statBar}>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>
                                {user.dob ? moment().diff(moment(toDateInput(user.dob), 'YYYY-MM-DD'), 'years') : '—'}
                            </span>
                            <span className={styles.statLabel}>Age</span>
                        </div>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>
                                {Math.round(user.attendancePercentage * 100)}%
                            </span>
                            <span className={styles.statLabel}>Attendance</span>
                        </div>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>{user.truency ?? '—'}</span>
                            <span className={styles.statLabel}>Truancy</span>
                        </div>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>
                                {user.picturePermission ? '✓' : '✗'}
                            </span>
                            <span className={styles.statLabel}>Picture</span>
                        </div>
                        {/* Whether they have been through the new registration process. Ungated
                            and cheap — it says nothing about *what* they consented to, only that
                            the conversation happened. */}
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>
                                {user.hasNewRegistration ? '✓' : '✗'}
                            </span>
                            <span className={styles.statLabel}>Registered</span>
                        </div>
                    </div>

                    {hasRelationships && (
                        <div className={styles.infoGrid} style={{ marginBottom: '0.75em' }}>
                            {user.buddies.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Buddy</span>
                                    <div className={styles.relatedPeople}>
                                        {user.buddies.map((b: any) => <RelatedPerson key={b.id} person={b} />)}
                                    </div>
                                </>
                            )}
                            {user.parents.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Parents</span>
                                    <div className={styles.relatedPeople}>
                                        {user.parents.map((b: any) => (
                                            <RelatedPerson key={b.id} person={b} editMode={editMode}
                                                onRemove={removeRelationship(b.id, 'PARENT')} />
                                        ))}
                                    </div>
                                </>
                            )}
                            {user.siblings.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Siblings</span>
                                    <div className={styles.relatedPeople}>
                                        {user.siblings.map((b: any) => (
                                            <RelatedPerson key={b.id} person={b} editMode={editMode}
                                                onRemove={removeRelationship(b.id, 'SIBLING')} />
                                        ))}
                                    </div>
                                </>
                            )}
                            {user.children.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Children</span>
                                    <div className={styles.relatedPeople}>
                                        {user.children.map((b: any) => (
                                            <RelatedPerson key={b.id} person={b} editMode={editMode}
                                                onRemove={removeRelationship(b.id, 'PARENT')} />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className={styles.infoGrid}>
                        <span className={styles.infoLabel}>Address</span>
                        <span className={styles.infoValue}>
                            {canEdit ? (
                                <Input
                                    fluid
                                    placeholder="Address"
                                    value={address}
                                    onChange={e => setAddress(e.target.value)}
                                    onBlur={() => commitAddress(address, user.address || '')}
                                />
                            ) : user.address}
                        </span>

                        <span className={styles.infoLabel}>Phone</span>
                        <span className={styles.infoValue}>
                            <UserPhoneNumber userId={user.id} inEditMode={editMode} />
                        </span>

                        {/* Beside Phone because it is the other way of reaching somebody — and
                            unlike the fields around it, this one is often the *only* way: the
                            members whose usernames were never addresses have nothing on file
                            at all, so no password reset can reach them. Gated by the server's
                            `canAddEmail`, never by a role guessed here. */}
                        <span className={styles.infoLabel}>Email</span>
                        <span className={styles.infoValue}>
                            <PersonEmail
                                userId={user.id}
                                email={user.email}
                                canAddEmail={user.canAddEmail}
                                onSaved={refetch}
                            />
                        </span>

                        <span className={styles.infoLabel}>Date of Birth</span>
                        <span className={styles.infoValue}>
                            {canEdit ? (
                                <Input
                                    fluid
                                    type="date"
                                    className={styles.dateField}
                                    min={DOB_MIN}
                                    max={localToday()}
                                    value={dob}
                                    onChange={e => setDob(e.target.value)}
                                    onBlur={handleDobBlur}
                                />
                            ) : formatDob(user.dob)}
                        </span>

                        {/* Gender was captured on the registration form but never shown or editable
                            here, so re-registering an existing person could not record it. It also
                            sets the ISO 5218 biologicalSex server-side, as the form does. */}
                        <span className={styles.infoLabel}>Gender</span>
                        <span className={styles.infoValue}>
                            {canEdit ? (
                                <select
                                    aria-label="Gender"
                                    value={user.gender || ''}
                                    onChange={(e) => commitGender(e.target.value, user.gender || '')}
                                >
                                    <option value="">—</option>
                                    {GENDER_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            ) : (GENDER_OPTIONS.find((o) => o.value === user.gender)?.label ?? '—')}
                        </span>

                        {isAdmin && (
                            <>
                                <span className={styles.infoLabel}>Reg. Document</span>
                                <span className={styles.infoValue}>
                                    {user.haveRegistrationDocument ? 'Yes' : 'No'}
                                    {user.registrationDocumentUrl && (
                                        <> — <a target="_blank" href={user.registrationDocumentUrl} rel="noopener noreferrer">View</a></>
                                    )}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Signing in.
                    Sits next to the email field above because the two are the same subject —
                    how you reach this person, and how this person reaches Joyya. The card is
                    its own gate: `canCreateLogin` is the server's answer to "may this viewer
                    give this person a login", folding the CREATE_LOGIN capability, a live
                    membership here, and the person having neither a username nor a password.
                    Deriving any of it on this side would be a copy that goes stale, and the
                    failure mode is a button that throws when pressed. */}
                {(user.canCreateLogin || loginResult || isAdmin) && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Signing in</div>
                        {loginResult ? (
                            <GiveLoginOutcome
                                result={loginResult}
                                name={user.preferredName || user.name}
                                onClose={() => { setLoginResult(null); refetch(); }}
                            />
                        ) : givingLogin ? (
                            <GiveLogin
                                userId={user.id}
                                name={user.name}
                                email={user.email}
                                onDone={(result) => { setGivingLogin(false); setLoginResult(result); }}
                                onCancel={() => setGivingLogin(false)}
                            />
                        ) : user.canSignIn ? (
                            // The answer to "why can't they get in?" — which is the question an
                            // admin standing here is usually holding. Deliberately not the
                            // username: which identifier somebody uses stays behind the
                            // existing `username` gate, and nothing here needs it.
                            <p style={{ color: '#767676', margin: 0 }}>
                                {user.preferredName || user.name} has an account and can sign in.
                            </p>
                        ) : user.canCreateLogin ? (
                            <>
                                <p style={{ color: '#767676' }}>
                                    {user.preferredName || user.name} has no way to sign in yet.
                                </p>
                                <Button size="small" primary onClick={() => setGivingLogin(true)}>
                                    Give login
                                </Button>
                            </>
                        ) : (
                            // **The third state, and the one that is easy to forget exists.** A
                            // username but no password: invited and not yet accepted. Both the
                            // other branches are false here, so without this the card simply
                            // vanishes the moment an invitation is sent — which reads as "that
                            // did not work" to the person who just sent it.
                            <p style={{ color: '#767676', margin: 0 }}>
                                {user.preferredName || user.name} has been given a username but
                                has not set a password yet. Progress is on{' '}
                                <Link to={PATHS.INVITES}>Invite people</Link>.
                            </p>
                        )}
                    </div>
                )}

                {/* Consent & registration.
                    `user.registration` is null for anyone the server will not show it to, and
                    `ConsentSectionCard` returns nothing when there is neither a record nor the
                    right to create one — so this card is its own gate. */}
                {(user.registration || canEdit) && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Consent &amp; registration</div>
                        <ConsentSectionCard
                            registration={user.registration}
                            personId={personId}
                            canEdit={canEdit}
                            onRefetch={refetch}
                        />
                    </div>
                )}

                {/* Supporting documents — thumbprint signatures, permission slips, scans of the
                    registration form. Empty for a viewer who may not see them, so the presence
                    of this card is decided server-side. */}
                {((user.supportingDocuments?.length ?? 0) > 0 || canEdit) && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Supporting documents</div>
                        <SupportingDocumentsCard
                            documents={user.supportingDocuments ?? []}
                            personId={personId}
                            canEdit={canEdit}
                            onRefetch={refetch}
                        />
                    </div>
                )}

                {/* Tags (edit mode only) */}
                {canEdit && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Profile Tags</div>
                        <PersonTags
                            tags={user.tags}
                            refetchPerson={() => refetch({ variables: { personId } })}
                        />
                    </div>
                )}

                {/* Current Block Attendance */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>Current Block Attendance</div>
                    <CurrentBlockSection
                        attendances={user.currentCourseAttendances}
                        personId={personId}
                    />
                </div>

                {/* Past Classes */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>Past Classes</div>
                    <CoursesSection courses={user.courses} personId={personId} />
                </div>

                {/* Classes Teaching */}
                {user.coursesTeaching.length > 0 && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Classes Teaching</div>
                        <TeachingSection courses={user.coursesTeaching} />
                    </div>
                )}

                {/* Permissions moved to /admin/roles (4B.5).
                    Access is granted by role now, and the handful of people holding access that
                    comes from no role are listed there in one place — rather than one profile at
                    a time, where in practice nobody found them. Removing this section was only
                    safe once that list existed: it was the sole surface showing those grants. */}

                {/* Danger Zone (edit mode only) */}
                {canEdit && !isCenterManager && (
                    <div className={styles.card}>
                        <div className={styles.dangerTitle}>Danger Zone</div>
                        <Button color="red" onClick={archiveUser}>Archive Person</Button>
                    </div>
                )}

            </div>
        </ResponsiveContainer>
    );
};

export { AdminProfileView };
